import React, { useEffect, useState } from "react";
import { CUSTOMER_SERVICE_PHONE_NUMBER } from "Utils/Constants";
import useSessionStorage from "Utils/Hooks/useSessionStorage";
import useVipApiFetcher from "Utils/Hooks/useVipApiFetcher";
import { faro } from "@grafana/faro-react";
import { Box, CircularProgress, Link } from "@mui/material";
import { Button, TextField } from "@pav/pav-products-design-system";
import SecurityAlertMessage from "./SecurityAlertMessage";
import { Controller, useForm } from "react-hook-form";

const OtpValidation = ({ otpSession, verified }) => {
  const [session] = useSessionStorage("vip-patron-session");
  const vipApi = useVipApiFetcher();
  const [newCodeSent, sendNewCode] = useState(null);
  const [resendIsLoading, setResendIsLoading] = useState(null);
  const [untrustedDevice, setDeviceAsUntrusted] = useState(null);
  const [currentOtpSession, updatedOtpSession] = useState(otpSession);
  const [totalFailedOtps, setTotalFailedOtps] = useState({
    otpSent: 1,
    otpSentAttempt: 0,
  });
  const [currentError, setCurrentError] = useState({
    otpError: { attempts: 0, msg: "" },
  });

  const {
    reset,
    handleSubmit,
    control,
    watch,
    formState: { errors, isLoading, isValidating, isSubmitting },
  } = useForm({
    mode: "onBlur",
    reValidateMode: "onBlur",
    criteriaMode: "all",
    defaultValues: {
      verificationCode: "",
    },
  });

  const verificationCode = watch("verificationCode");

  const onSubmit = async (data) => {
    setCurrentError((prevState) => {
      return {
        ...prevState,
        otpError: {
          attempts: currentError.otpError.attempts,
          msg: undefined,
        },
      };
    });
    try {
      const response = await vipApi.fetch(
        "/api/patronsession/verifyotp",
        "POST",
        {
          oneTimePasscode: data.verificationCode,
          payloadSessionId: currentOtpSession.payloadSessionId,
        },
      );
      const otpResponse = await response.json();
      switch (response.status) {
        case 200:
          verified(true);
          break;
        case 400:
          setCurrentError((prevState) => {
            return {
              ...prevState,
              otpError: {
                attempts: Number(currentError.otpError.attempts) + 1,
                msg: otpResponse.map((item) => item.message).join("\n"),
              },
            };
          });
          setTotalFailedOtps((prevState) => {
            return {
              ...prevState,
              otpSent:
                newCodeSent && Number(currentError.otpError.attempts) === 5
                  ? Number(totalFailedOtps.otpSent) + 1
                  : Number(totalFailedOtps.otpSent),
              otpSentAttempt:
                Number(totalFailedOtps.otpSentAttempt) <= 5
                  ? Number(totalFailedOtps.otpSentAttempt) + 1
                  : 1,
            };
          });
          reset();
          faro.api.pushError(new Error("Verifying OTP Failure"), {
            context: otpResponse,
          });
          break;
        default:
          faro.api.pushError(new Error("Verifying OTP Error"), {
            context: response,
          });
          throw response;
      }
    } catch (err) {
      console.log("Error validating OTP: ", err);
      faro.api.pushError(new Error("Device ID Verify OTP Network Error"), {
        context: err,
      });
    }
  };

  const resendCode = async () => {
    sendNewCode(true);
    setResendIsLoading(true);
    reset();
    setTotalFailedOtps((prevState) => {
      return {
        ...prevState,
        otpSent:
          Number(totalFailedOtps.otpSent) < 3
            ? Number(totalFailedOtps.otpSent) + 1
            : Number(totalFailedOtps.otpSent),
        otpSentAttempt: 0,
      };
    });
    setCurrentError({ otpError: { attempts: 0, msg: "" } });
    try {
      const response = await vipApi.fetch(
        "/api/patronsession/sendotp",
        "POST",
        {
          sessionid: session.sessionId,
        },
      );
      if (response.status === 200) {
        const otpResponse = await response.json();
        updatedOtpSession((prev) => ({
          ...prev,
          failedAttemptThreshold: otpResponse.failedAttemptThreshold,
          oneTimePasscodePending: otpResponse.oneTimePasscodePending,
          payloadSessionId: otpResponse.payloadSessionId,
        }));
      } else {
        faro.api.pushError(new Error("Device ID Resend OTP Error"), {
          context: response,
        });
      }
    } catch (err) {
      console.log("Error resending OTP: ", err);
      faro.api.pushError(new Error("Device ID Resend OTP Network Error"), {
        context: err,
      });
    } finally {
      setResendIsLoading(false);
    }
  };

  useEffect(() => {
    if (
      Number(totalFailedOtps.otpSent) === 3 &&
      Number(totalFailedOtps.otpSentAttempt) === 5
    ) {
      verified(false);
      setDeviceAsUntrusted(true);
    }
  }, [totalFailedOtps]);

  return (
    <Box>
      <Box>
        {untrustedDevice === null && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "var(--spacing-md)",
            }}
          >
            <Box className="header">Enter verification code</Box>
            <Box className="sub-title">
              Sent to +1 XXX-XXX-{session.phoneLast4}
            </Box>
          </Box>
        )}
        <Box>
          {!untrustedDevice && (
            <Box>
              <form onSubmit={handleSubmit(onSubmit)} noValidate>
                <Box
                  sx={{
                    marginBottom: "var(--spacing-md)",
                  }}
                >
                  <Controller
                    name="verificationCode"
                    rules={{
                      pattern: {
                        value: /^\d+$/,
                        message: "Please enter only numbers.",
                      },
                      minLength: {
                        value: 6,
                        message: "Must be 6 digits.",
                      },
                      maxLength: {
                        value: 6,
                        message: "Must be 6 digits.",
                      },
                    }}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="VERIFICATION CODE"
                        error={
                          currentError.otpError.msg ||
                          errors.verificationCode?.message ||
                          (!untrustedDevice &&
                            currentError.otpError.attempts === 5) ||
                          untrustedDevice
                        }
                        errorText={
                          !untrustedDevice &&
                          currentError.otpError.attempts === 5
                            ? "The maximum attempts are reached for the verification code. To try again, please resend a new code below."
                            : untrustedDevice
                              ? "You have failed verification, we will close this screen and send you to the cashier's page in 20 seconds."
                              : currentError.otpError.msg ===
                                  "The attempt failed:  The submitted code does not match."
                                ? "The submitted code does not match."
                                : currentError.otpError.msg
                                  ? currentError.otpError.msg
                                  : errors?.verificationCode?.message
                        }
                        autoComplete="off"
                        required
                        fullWidth
                        disabled={
                          currentError.otpError.attempts === 5 ||
                          resendIsLoading ||
                          isLoading
                        }
                      />
                    )}
                  />
                </Box>
                <Box
                  sx={{
                    marginBottom: "32px",
                  }}
                >
                  <SecurityAlertMessage />
                </Box>
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: "10px" }}
                >
                  <Button
                    padding="var(--spacing-lg)"
                    fullWidth
                    type="submit"
                    disabled={
                      !verificationCode ||
                      isLoading ||
                      isSubmitting ||
                      isValidating ||
                      verificationCode.length !== 6
                    }
                    startIcon={
                      (isLoading || isSubmitting || isValidating) && (
                        <CircularProgress color="inherit" size="1.5rem" />
                      )
                    }
                  >
                    Submit
                  </Button>
                  <Button
                    padding="var(--spacing-lg)"
                    fullWidth
                    buttonType="tertiary"
                    onClick={resendCode}
                    disabled={totalFailedOtps.otpSent === 4}
                    startIcon={
                      resendIsLoading && (
                        <CircularProgress color="inherit" size="1.5rem" />
                      )
                    }
                  >
                    {resendIsLoading ? "SENDING CODE" : "RESEND CODE"}
                  </Button>
                </Box>
              </form>
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  position: "fixed",
                  bottom: 0,
                  left: 0,
                  flexDirection: "column",
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    padding: "16px var(--spacing-4xl)",
                    background: "var(--color-surface-background-secondary)",
                    textAlign: "center",
                    fontSize: "var(--caption-size-medium)",
                    fontWeight: 400,
                  }}
                >
                  <span>
                    If you don't have access to this phone number, please call
                    Customer Support at{" "}
                  </span>
                  <Link
                    href={`tel:${CUSTOMER_SERVICE_PHONE_NUMBER}`}
                    sx={{
                      color: "var(--color-text-link)",
                      fontWeight: 700,
                    }}
                  >
                    1-800-500-1973
                  </Link>
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};
export default OtpValidation;
